import { useState, useCallback } from 'react'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { useContract } from './useContract.ts'
import { useWallet } from './useWallet.ts'
import type {
  Subscription,
  PricingInfo,
  ContractWriteResult,
} from '@/services/contract/contract.types.ts'
import { type SubscriptionTier } from '@/services/contract/contract.types.ts'

interface UseSubscriptionState {
  subscription: Subscription | null
  isActive: boolean | null
  pricing: PricingInfo | null
  loading: boolean
  error: string | null
}

interface UseSubscriptionReturn extends UseSubscriptionState {
  fetchSubscription: () => Promise<void>
  checkActive: () => Promise<void>
  fetchPricing: () => Promise<void>
  subscribe: (tier: SubscriptionTier) => Promise<ContractWriteResult | null>
  renewSubscription: (
    tier: SubscriptionTier,
  ) => Promise<ContractWriteResult | null>
  approveAndSubscribe: (
    tier: SubscriptionTier,
  ) => Promise<ContractWriteResult | null>
  approveAndRenew: (
    tier: SubscriptionTier,
  ) => Promise<ContractWriteResult | null>
}

export function useSubscription(): UseSubscriptionReturn {
  const contract = useContract()
  const { address } = useWallet()

  const [state, setState] = useState<UseSubscriptionState>({
    subscription: null,
    isActive: null,
    pricing: null,
    loading: false,
    error: null,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))

  const fetchSubscription = useCallback(async () => {
    if (!address) return
    setLoading()
    try {
      const subscription = await contract.getSubscription(
        address as `0x${string}`,
      )
      setState((s) => ({ ...s, subscription, loading: false }))
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    }
  }, [address, contract])

  const checkActive = useCallback(async () => {
    if (!address) return
    setLoading()
    try {
      const isActive = await contract.isSubscriptionActive(
        address as `0x${string}`,
      )
      setState((s) => ({ ...s, isActive, loading: false }))
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    }
  }, [address, contract])

  const fetchPricing = useCallback(async () => {
    setLoading()
    try {
      const pricing = await contract.getPricing()
      setState((s) => ({ ...s, pricing, loading: false }))
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    }
  }, [contract])

  const subscribe = useCallback(
    async (tier: SubscriptionTier): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.subscribe(tier)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const renewSubscription = useCallback(
    async (tier: SubscriptionTier): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.renewSubscription(tier)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const approveAndSubscribe = useCallback(
    async (tier: SubscriptionTier): Promise<ContractWriteResult | null> => {
      if (!address) return null
      setLoading()
      try {
        const pricing = await contract.getPricing()
        const priceByTier: Record<SubscriptionTier, bigint> = {
          1: pricing.oneYearPrice,
          2: pricing.oneMonthPrice,
        }
        const price = priceByTier[tier]
        const { address: contractAddr } = (
          await import('@/services/contract/contract.config.ts')
        ).contractConfig
        await contract.approveToken(contractAddr, price)
        const result = await contract.subscribe(tier)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [address, contract],
  )

  const approveAndRenew = useCallback(
    async (tier: SubscriptionTier): Promise<ContractWriteResult | null> => {
      if (!address) return null
      setLoading()
      try {
        const pricing = await contract.getPricing()
        const priceByTier: Record<SubscriptionTier, bigint> = {
          1: pricing.oneYearPrice,
          2: pricing.oneMonthPrice,
        }
        const price = priceByTier[tier]
        const { address: contractAddr } = (
          await import('@/services/contract/contract.config.ts')
        ).contractConfig
        await contract.approveToken(contractAddr, price)
        const result = await contract.renewSubscription(tier)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [address, contract],
  )

  return {
    ...state,
    fetchSubscription,
    checkActive,
    fetchPricing,
    subscribe,
    renewSubscription,
    approveAndSubscribe,
    approveAndRenew,
  }
}
